(() => {
  const sectionHandles = ['cart-count-badge', 'offcanvas-cart', 'cm-cart-page']
  // Mağazanın çeviri dosyasındaki `cart.error` metni; locale yüklenmemişse
  // İngilizce yedeğe düşer.
  const fallbackError = () => window.theme?.locales?.cart?.error ||
    'Cart could not be updated. Please try again.'

  class CartRequestError extends Error {
    constructor (message, responseData = null) {
      super(message || fallbackError())
      this.name = 'CartRequestError'
      this.responseData = responseData
    }
  }

  // Shopify Ajax Cart API'si başarılı yanıtları `Content-Type: text/javascript`
  // ile döner, `application/json` yalnızca hata gövdelerinde kullanılır.
  // Content-type'a bakmak her başarılı sepet isteğini hataya çevirdiği için
  // karar gövdenin ayrıştırılabilirliğine göre verilir.
  const parseResponse = async response => {
    let payload

    try {
      payload = await response.text()
    } catch (error) {
      throw new CartRequestError()
    }

    if (!payload) return null

    try {
      return JSON.parse(payload)
    } catch (error) {
      throw new CartRequestError()
    }
  }

  const getErrorMessage = data => {
    if (!data) return fallbackError()
    return data.description || data.message || fallbackError()
  }

  class CommuniteCartAPI {
    constructor () {
      this.changeQueue = Promise.resolve()
    }

    async request (url, options) {
      let response

      try {
        response = await fetch(url, options)
      } catch (error) {
        throw new CartRequestError()
      }

      const data = await parseResponse(response)

      if (!response.ok) {
        throw new CartRequestError(getErrorMessage(data), data)
      }

      if (data?.sections) {
        this.renderSections(data.sections)
      }

      return data
    }

    async add (formData) {
      formData.set('sections', sectionHandles.join(','))
      formData.set('sections_url', window.location.pathname)

      const data = await this.request(`${window.Shopify.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })

      window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }))
      this.announce(window.theme?.locales?.cart?.added || 'Item added to cart.')
      return data
    }

    async addItems (items) {
      const data = await this.request(`${window.Shopify.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items,
          sections: sectionHandles,
          sections_url: window.location.pathname
        })
      })

      window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }))
      this.announce(window.theme?.locales?.cart?.added || 'Items added to cart.')
      return data
    }

    change (id, quantity) {
      const performChange = async () => {
        const data = await this.request(`${window.Shopify.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id,
            quantity,
            sections: sectionHandles,
            sections_url: window.location.pathname
          })
        })

        window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data }))
        this.announce(window.theme?.locales?.cart?.updated || 'Cart updated.')
        return data
      }
      const pendingChange = this.changeQueue.then(performChange, performChange)

      this.changeQueue = pendingChange.catch(() => {})
      return pendingChange
    }

    async update (updates) {
      const data = await this.request(`${window.Shopify.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...updates,
          sections: sectionHandles,
          sections_url: window.location.pathname
        })
      })

      window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data }))
      this.announce(window.theme?.locales?.cart?.updated || 'Cart updated.')
      return data
    }

    renderSections (sections = {}) {
      if (sections['cart-count-badge']) {
        const countDocument = new DOMParser().parseFromString(sections['cart-count-badge'], 'text/html')
        const newBadge = countDocument.querySelector('.cart-count-badge')

        if (newBadge) {
          document.querySelectorAll('.cart-count-badge').forEach(badge => {
            badge.replaceWith(newBadge.cloneNode(true))
          })
        }
      }

      if (sections['offcanvas-cart']) {
        const drawerDocument = new DOMParser().parseFromString(sections['offcanvas-cart'], 'text/html')
        const newContainer = drawerDocument.querySelector('#offcanvas-cart cart-container')
        const currentContainer = document.querySelector('#offcanvas-cart cart-container')

        if (newContainer && currentContainer) {
          currentContainer.replaceWith(newContainer)
        }
      }

      if (sections['cm-cart-page']) {
        const pageDocument = new DOMParser().parseFromString(sections['cm-cart-page'], 'text/html')
        const newPage = pageDocument.querySelector('[data-cm-cart-page]')
        const currentPage = document.querySelector('[data-cm-cart-page]')

        if (newPage && currentPage) {
          currentPage.replaceWith(newPage)
        }
      }

      window.dispatchEvent(new CustomEvent('kt.cart.reloaded'))
    }

    announce (message) {
      document.querySelectorAll('[data-cm-cart-live]').forEach(region => {
        region.textContent = ''
        window.requestAnimationFrame(() => {
          region.textContent = message
        })
      })
    }

    showDrawer () {
      const drawer = document.querySelector('#offcanvas-cart')

      if (drawer && window.bootstrap?.Offcanvas) {
        document.querySelectorAll('.offcanvas.show').forEach(openDrawer => {
          if (openDrawer !== drawer) {
            window.bootstrap.Offcanvas.getOrCreateInstance(openDrawer).hide()
          }
        })
        window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show()
      }
    }
  }

  const cartAPI = new CommuniteCartAPI()
  window.CommuniteCart = cartAPI

  class CartContainer extends HTMLElement {
    connectedCallback () {
      this.controller?.abort()
      this.controller = new AbortController()
      this.mutationPending = false
      const { signal } = this.controller

      this.addEventListener('click', event => this.onClick(event), { signal })
      this.addEventListener('change', event => this.onChange(event), { signal })

      if (new URLSearchParams(window.location.search).has('cart')) {
        window.requestAnimationFrame(() => cartAPI.showDrawer())
      }
    }

    disconnectedCallback () {
      this.controller?.abort()
    }

    async add (formData) {
      this.clearError()

      try {
        const data = await cartAPI.add(formData)
        cartAPI.showDrawer()
        return data
      } catch (error) {
        this.showError(error.message)
        cartAPI.showDrawer()
        throw error
      }
    }

    async change (id, quantity) {
      if (
        this.mutationPending ||
        !id ||
        !Number.isInteger(quantity) ||
        quantity < 0
      ) return

      this.mutationPending = true
      this.setLoading(true)
      this.clearError()

      try {
        return await cartAPI.change(id, quantity)
      } catch (error) {
        this.showError(error.message)
        throw error
      } finally {
        this.mutationPending = false
        if (this.isConnected) this.setLoading(false)
      }
    }

    reloadCartElements (sections) {
      cartAPI.renderSections(sections)
    }

    onClick (event) {
      const decrement = event.target.closest('[data-cm-quantity-minus]')
      const increment = event.target.closest('[data-cm-quantity-plus]')
      const remove = event.target.closest('[data-cm-cart-remove]')

      if (remove) {
        this.change(remove.dataset.lineItemKey, 0).catch(() => {})
        return
      }

      const button = decrement || increment
      if (!button) return

      const input = button.closest('[data-cm-quantity]')?.querySelector('[name="updates[]"]')
      if (!input) return

      const direction = increment ? 1 : -1
      const currentQuantity = this.confirmedQuantity(input)
      const nextQuantity = Math.max(0, currentQuantity + direction)

      if (nextQuantity !== currentQuantity) {
        this.change(input.dataset.lineItemKey, nextQuantity).catch(() => {})
      }
    }

    onChange (event) {
      const input = event.target.closest('[name="updates[]"]')
      if (!input) return

      const currentQuantity = this.confirmedQuantity(input)
      const nextQuantity = Number(input.value)

      input.value = String(currentQuantity)
      if (!Number.isInteger(nextQuantity) || nextQuantity < 0 || nextQuantity === currentQuantity) return

      this.change(input.dataset.lineItemKey, nextQuantity).catch(() => {})
    }

    confirmedQuantity (input) {
      const quantity = Number(input.dataset.currentQuantity)
      return Number.isInteger(quantity) && quantity >= 0 ? quantity : 0
    }

    setLoading (loading) {
      this.classList.toggle('loading', loading)
      this.setAttribute('aria-busy', String(loading))

      this.querySelectorAll(
        '[data-cm-quantity-minus], [data-cm-quantity-plus], [data-cm-cart-remove], [name="updates[]"]'
      ).forEach(control => {
        if (loading) {
          control.dataset.cmPreviouslyDisabled = String(control.disabled)
          control.disabled = true
        } else {
          control.disabled = control.dataset.cmPreviouslyDisabled === 'true'
          delete control.dataset.cmPreviouslyDisabled
        }
      })
    }

    clearError () {
      const status = this.querySelector('[data-cm-cart-error]')
      if (status) status.hidden = true
    }

    showError (message) {
      const status = this.querySelector('[data-cm-cart-error]')
      const text = status?.querySelector('[data-cm-cart-error-message]')

      if (status && text) {
        text.textContent = message || fallbackError()
        status.hidden = false
        status.focus?.()
      }

      cartAPI.announce(message || fallbackError())
      window.dispatchEvent(new CustomEvent('cm:cart:error', {
        detail: { message: message || fallbackError() }
      }))
    }
  }

  if (!customElements.get('cart-container')) {
    customElements.define('cart-container', CartContainer)
  }

  class CartNote extends HTMLElement {
    connectedCallback () {
      this.controller?.abort()
      this.controller = new AbortController()
      this.querySelector('button')?.addEventListener('click', () => this.save(), {
        signal: this.controller.signal
      })
    }

    disconnectedCallback () {
      this.controller?.abort()
    }

    async save () {
      const input = this.querySelector('textarea')
      const button = this.querySelector('button')
      if (!input || !button) return

      const originalLabel = button.innerHTML
      button.disabled = true
      button.setAttribute('aria-busy', 'true')

      try {
        await cartAPI.update({ note: input.value })
        button.textContent = button.dataset.textNoteSaved
      } catch (error) {
        this.closest('cart-container')?.showError(error.message)
      } finally {
        button.disabled = false
        button.setAttribute('aria-busy', 'false')
        window.setTimeout(() => {
          if (button.isConnected) button.innerHTML = originalLabel
        }, 3000)
      }
    }
  }

  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', CartNote)
  }
})()
